import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiBarChart2,
  FiPlusCircle,
  FiAlertTriangle,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiArrowRight,
  FiPlus,
  FiShoppingBag,
  FiClock,
  FiCode,
  FiRefreshCw,
  FiDollarSign,
  FiMessageSquare,
  FiFileText
} from 'react-icons/fi';
import { 
  IoMdCube, 
  IoMdWarning,
  IoMdCheckmarkCircle 
} from 'react-icons/io';
import { Link } from 'react-router-dom';
import { HiExclamation } from 'react-icons/hi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend,
  ScatterChart,
  Scatter,
  ZAxis
} from 'recharts';
import { useUser } from '@clerk/clerk-react';
import WelcomeToArms from '../components/WelcomeToArms';
import UserActivityChart from '../components/UserActivityChart';
import ResourceUtilization from '../components/ResourceUtilization';
import SystemResourcesOverview from '../components/SystemResourcesOverview';
import OrganizationSelector from '../components/OrganizationSelector';
import AlwaysShowWarning from '../components/AlwaysShowWarning';
import { fetchProjects, fetchRuns } from '../services/api';
import { useCache } from '../contexts/CacheContext';



// Card component for reusable cards
const Card = ({ children, className }) => (
  <div className={`bg-white p-6 rounded-xl shadow-lg border border-gray-100 h-full hover:shadow-xl transition-shadow duration-300 ${className || ''}`}>
    {children}
  </div>
);

// StatCard component for the top stats
const StatCard = ({ title, value, icon, indicator, indicatorText, indicatorColor }) => (
  <Card>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-gray-600 font-medium text-sm">{title}</h2>
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-100">
        {icon}
      </div>
    </div>
    <div className="flex items-baseline">
      <span className="text-2xl font-bold text-gray-900">{value}</span>
    </div>
    {indicator && (
      <div className="mt-2 flex items-center">
        <div className={indicatorColor}>
          {indicatorText}
        </div>
      </div>
    )}
  </Card>
);

// VisualizationCard component for chart sections with standardized dimensions and overflow handling
const VisualizationCard = ({ title, children, action, subtitle, className, height = "320px" }) => (
  <div className={`w-full ${className || ''}`}>
    <Card className="h-full overflow-hidden">
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-start mb-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 mb-1 truncate">{title}</h2>
            {subtitle && <p className="text-sm text-gray-600 truncate">{subtitle}</p>}
          </div>
          {action && <div className="ml-4 flex-shrink-0">{action}</div>}
        </div>
        <div className="flex-grow min-h-0 overflow-hidden">
          <div className="w-full h-full overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {children}
          </div>
        </div>
      </div>
    </Card>
  </div>
);

// Button component
const Button = ({ children, variant = 'primary', className, icon, onClick }) => {
  const getButtonClasses = () => {
    switch (variant) {
      case 'primary':
        return 'bg-blue-600 text-white hover:bg-blue-700';
      case 'outline':
        return 'border border-blue-600 text-blue-600 hover:bg-blue-50';
      case 'light':
        return 'bg-blue-50 text-blue-600 hover:bg-blue-100';
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700';
    }
  };

  return (
    <button 
      className={`px-4 py-2 rounded-md flex items-center font-inter ${getButtonClasses()} ${className || ''}`}
      onClick={onClick}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
};

// TokenUsageChart component
const TokenUsageChart = ({ data }) => {
  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value.toString();
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const estimatedCost = (value / 1000) * 0.002;
      
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold text-sm text-gray-900 mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Tokens:</span>
              <span className="text-sm font-bold text-gray-900">
                {value.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Cost:</span>
              <span className="text-sm font-semibold text-emerald-600">
                ${estimatedCost.toFixed(4)}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
      return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
          </div>
          <p className="text-gray-500 font-medium">No token usage data available</p>
          <p className="text-gray-400 text-sm mt-1">Start using tokens to see trends</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-white via-purple-50 to-pink-50 rounded-2xl p-4 shadow-lg border border-purple-100 overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
          margin={{ top: 20, right: 30, left: 60, bottom: 40 }}
            >
          <defs>
            <linearGradient id="tokenGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
              <stop offset="50%" stopColor="#EC4899" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#EF4444" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="25%" stopColor="#A855F7" />
              <stop offset="50%" stopColor="#EC4899" />
              <stop offset="75%" stopColor="#F472B6" />
              <stop offset="100%" stopColor="#EF4444" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="shadow">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#8B5CF6" floodOpacity="0.3"/>
            </filter>
          </defs>
          
          <CartesianGrid 
            strokeDasharray="12 12" 
            vertical={false} 
            stroke="#E5E7EB" 
            strokeOpacity={0.2}
          />
          
              <XAxis 
                dataKey="date" 
                axisLine={false} 
                tickLine={false}
            tick={{ 
              fontSize: 9, 
              fill: '#6B7280', 
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600
            }}
            dy={25}
            tickMargin={10}
            angle={0}
            textAnchor="middle"
            height={40}
            interval="preserveStartEnd"

          />
          
              <YAxis 
                axisLine={false} 
                tickLine={false} 
            tick={{ 
              fontSize: 10, 
              fill: '#6B7280', 
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600
            }}
            domain={[0, 'dataMax + dataMax * 0.15']}
                tickFormatter={formatYAxis}
                tickCount={6}
                dx={-5}
            tickMargin={5}

              />
          
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={30}
                wrapperStyle={{ 
                  fontSize: '11px', 
                  fontWeight: '700',
                  fontFamily: 'Inter, sans-serif',
                  color: '#374151'
                }}
                formatter={(value) => {
                  // Convert data key to readable label
                  if (value === 'tokens') return 'Token Usage';
                  if (value === 'totalTokens') return 'Total Tokens';
                  return value.charAt(0).toUpperCase() + value.slice(1);
                }}
              />
          
              {Object.keys(data[0])
                .filter(key => key !== 'date' && key !== 'cost')
                .map((key, index) => (
                  <Line 
                    key={index}
                    type="monotone" 
                    dataKey={key} 
                    name={key === 'tokens' ? 'Token Usage' : key === 'totalTokens' ? 'Total Tokens' : key.charAt(0).toUpperCase() + key.slice(1)}
                stroke="url(#lineGradient)"
                strokeWidth={3}
                fill="url(#tokenGradient)"
                fillOpacity={0.15}
                filter="url(#shadow)"
                dot={{
                  fill: '#8B5CF6',
                  stroke: '#ffffff',
                  strokeWidth: 2,
                  r: 5,
                  filter: "url(#glow)"
                }}
                activeDot={{
                  fill: '#EC4899',
                  stroke: '#ffffff',
                  strokeWidth: 3,
                  r: 7,
                  filter: "url(#glow)"
                }}
                className="hover:stroke-4 transition-all duration-300"
              />
            ))}
            </LineChart>
          </ResponsiveContainer>
          
          {/* Bottom Labels */}
          <div className="relative mt-3 pt-3 border-t border-gray-200">
            <div className="absolute bottom-2 left-0 text-sm text-gray-700 font-semibold">Token Usage</div>
            <div className="absolute bottom-2 right-0 text-sm text-gray-700 font-semibold">Date</div>
          </div>
        </div>
  );
};

// ProjectDistributionChart component - Enhanced Vertical Bar Chart
const ProjectDistributionChart = ({ data, projects }) => {
  // Prepare project data for vertical bar chart with better spacing
  const chartData = data.slice(0, 8).map(item => {
    const fullProject = projects.find(p => 
      (p.id === item.projectId) || 
      (p._id === item.projectId) || 
      (p.name === item.name)
    ) || {};
    
    return {
      name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name,
      fullName: item.name,
      runs: item.runs,
      tokens: item.totalTokens,
      cost: fullProject.total_cost || 
        Object.values(fullProject.costs || {}).reduce((a, b) => a + b, 0) || 0
    };
  });

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">No project distribution data available</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold text-sm text-gray-900 mb-2">{data.fullName}</p>
          <div className="space-y-1 text-xs text-gray-700">
            <div className="flex justify-between">
              <span className="font-medium">Runs:</span>
              <span className="text-blue-600 font-semibold">{data.runs}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Tokens:</span>
              <span className="text-green-600 font-semibold">{data.tokens.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Cost:</span>
              <span className="text-purple-600 font-semibold">${data.cost.toFixed(2)}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full overflow-hidden">
        <ResponsiveContainer width="100%" height="100%">
        <BarChart 
          data={chartData} 
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          barCategoryGap={8}
          barSize={Math.max(15, Math.min(25, 200 / chartData.length))}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} stroke="#f0f0f0" />
            <XAxis 
              dataKey="name" 
              type="category" 
            label={{ 
              value: 'Projects', 
              position: 'insideBottom', 
              offset: -10,
              style: { fontSize: '11px', fontWeight: 'bold' }
            }}
            tick={{ fontSize: 9, fill: '#6B7280' }}
            axisLine={false}
            tickLine={false}
            angle={chartData.length > 6 ? -45 : 0}
            textAnchor={chartData.length > 6 ? 'end' : 'middle'}
            height={chartData.length > 6 ? 80 : 60}
            interval={0}
            />
            <YAxis 
              type="number"
            tick={{ fontSize: 10, fill: '#6B7280' }}
            axisLine={false}
            tickLine={false}
            tickMargin={10}
            />
            <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="top" 
            height={30}
            wrapperStyle={{ fontSize: '12px', fontWeight: 'bold' }}
          />
            <Bar 
              dataKey="runs" 
            name="Number of Runs" 
              fill="#3B82F6" 
              radius={[4, 4, 0, 0]}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={`hsl(${210 + index * 30}, 70%, ${50 + index * 5}%)`}
            />
            ))}
          </Bar>
          </BarChart>
        </ResponsiveContainer>
        
        {/* Bottom Labels */}
        <div className="flex justify-center mt-2 pt-2 border-t border-gray-100">
          <div className="text-sm text-gray-600 font-semibold">Projects</div>
        </div>
    </div>
  );
};

// ModelWiseDistributionChart component - Enhanced with Better Color Contrast
const ModelWiseDistributionChart = ({ data }) => {
  const getModelColor = (model) => {
    // Enhanced color palette with better contrast and accessibility
    const colors = {
      // OpenAI Models
      'gpt-4': '#1E40AF',           // Deep blue - high contrast
      'gpt-4o': '#3B82F6',          // Bright blue
      'gpt-4o-large': '#6366F1',    // Indigo
      'gpt-4o-mini': '#06B6D4',     // Cyan
      'gpt-4o-mini-2024-07-18': '#06B6D4', // Cyan (same as gpt-4o-mini)
      'gpt-3.5-turbo': '#0EA5E9',   // Sky blue
      'gpt-3.5': '#0EA5E9',         // Sky blue (alternative)
      
      // Anthropic Models
      'claude': '#7C3AED',          // Violet
      'claude-3': '#A855F7',        // Purple
      'claude-3.5': '#C084FC',      // Light purple
      'claude-3-opus': '#A855F7',   // Purple
      'claude-3-sonnet': '#C084FC', // Light purple
      'claude-3-haiku': '#E879F9',  // Pink
      
      // Meta Models
      'llama-3': '#F59E0B',         // Amber
      'llama-2': '#F97316',         // Orange
      'llama': '#F97316',           // Orange (alternative)
      
      // Mistral Models
      'mistral-7b': '#EF4444',      // Red
      'mistral-8x7b': '#DC2626',    // Dark red
      'mistral': '#EF4444',         // Red (alternative)
      
      // Google Models
      'gemini': '#10B981',          // Emerald
      'gemini-pro': '#059669',      // Dark emerald
      'gemini-1.5': '#10B981',      // Emerald
      'gemini-1.5-pro': '#059669',  // Dark emerald
      
      // Other Models
      'palm': '#84CC16',            // Lime
      'palm-2': '#84CC16',          // Lime
      'cohere': '#F472B6',          // Pink
      'cohere-command': '#F472B6',  // Pink
      'anthropic': '#7C3AED',       // Violet
      'openai': '#3B82F6',          // Blue
      'meta': '#F59E0B',            // Amber
      'google': '#10B981',          // Emerald
      
      // Default fallback
      'unknown': '#6B7280'          // Gray
    };
    
    // Try exact match first
    const exactMatch = colors[model.toLowerCase()];
    if (exactMatch) return exactMatch;
    
    // Try partial matches for model families
    const modelLower = model.toLowerCase();
    for (const [key, color] of Object.entries(colors)) {
      if (modelLower.includes(key) && key !== 'unknown') {
        return color;
      }
    }
    
    // Generate a consistent color based on model name hash
    const hash = modelLower.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const hue = Math.abs(hash) % 360;
    const saturation = 70 + (Math.abs(hash) % 20); // 70-90%
    const lightness = 45 + (Math.abs(hash) % 15);  // 45-60%
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const getContrastColor = (backgroundColor) => {
    // Calculate contrast for better label visibility
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#FFFFFF';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const modelColor = getModelColor(data.model);
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-200">
          <div className="flex items-center mb-2">
            <div 
              className="w-4 h-4 rounded-sm mr-2" 
              style={{ backgroundColor: modelColor }}
            />
            <p className="font-semibold text-sm text-gray-900">{data.model}</p>
          </div>
          <div className="space-y-1 text-xs text-gray-700">
            <div className="flex justify-between">
              <span className="font-medium">Runs:</span>
              <span className="text-blue-600 font-semibold">{data.runs}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Tokens:</span>
              <span className="text-green-600 font-semibold">{data.tokens.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Cost:</span>
              <span className="text-purple-600 font-semibold">${data.cost.toFixed(5)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Usage:</span>
              <span className="text-orange-600 font-semibold">{data.percentage}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, fill }) => {
    if (percent < 0.08) return null; // Don't show labels for slices less than 8%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Use contrast color for better readability
    const textColor = getContrastColor(fill);

    return (
      <text 
        x={x} 
        y={y} 
        fill={textColor} 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="11"
        fontWeight="600"
        fontFamily="Inter, sans-serif"
        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Enhanced empty state check - ensure we have valid data with actual runs
  if (!data || data.length === 0 || data.every(item => item.runs === 0)) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">No model distribution data available</p>
          <p className="text-gray-400 text-sm mt-1">Start using AI models to see analytics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomLabel}
            outerRadius={Math.min(80, Math.max(60, 120 / data.length))}
            innerRadius={Math.min(25, Math.max(15, 40 / data.length))}
                fill="#8884d8"
                dataKey="runs"
            paddingAngle={data.length > 8 ? 1 : 2}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getModelColor(entry.model)} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="top" 
            height={data.length > 6 ? 60 : 30}
            wrapperStyle={{ fontSize: data.length > 8 ? '10px' : '12px', fontWeight: 'bold' }}
            layout={data.length > 8 ? 'horizontal' : 'vertical'}
          />
            </PieChart>
          </ResponsiveContainer>
        </div>
  );
};

// AvailableModelsDisplay component - Shows all available LLM models with distinct colors


const Dashboard = () => {
  // Get user from Clerk
  const { user, isLoaded } = useUser();
  
  // Get cache functions
  const { getProjects, getRuns, clearUserCache } = useCache();
  
  // Remove config and DB state
  const [timeFilter, setTimeFilter] = useState('All Time');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  

  const [projectsData, setProjectsData] = useState([]);
  const [runsData, setRunsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Format number helper
  const formatNumber = useCallback((num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  }, []);
  
  // Derived state for dashboard metrics
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [avgLatency, setAvgLatency] = useState(0);
  const [tokenUsageData, setTokenUsageData] = useState([]);
  const [modelDistributionData, setModelDistributionData] = useState([]);
  const [modelWiseData, setModelWiseData] = useState([]);
  
  // Function to refresh all dashboard data
  const refreshDashboardData = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      // Clear cache and fetch fresh data
      clearUserCache(user.id);
      
      // Force refresh by calling API directly
      const projectsResponse = await fetchProjects(null, user.id, false);
      const runsResponse = await fetchRuns(user.id, false);
      
      // Extract data from Lambda responses (app-data returns projects with nested runs; use .projects only)
      const projects = projectsResponse?.data?.projects ?? projectsResponse?.projects ?? [];
      const runsData = runsResponse?.data?.projects ?? runsResponse?.projects ?? [];
      
      // Extract individual runs from the runs data
      const runs = [];
      runsData.forEach(project => {
        if (project && project.runs && Array.isArray(project.runs)) {
          project.runs.forEach(run => {
            if (run) {
              const runWithProject = {
                ...run,
                project_id: project.project_id,
                project_name: project.project,
                organization_id: project.organization_id,
                organization_name: project.organization_name,
                api_key: project.api_key
              };
              runs.push(runWithProject);
            }
          });
        }
      });
      
      setProjectsData(projects);
      setRunsData(runs);
      processData(projects, runs);
    } catch (err) {
      console.error('Dashboard refresh error:', err);
      // Don't show error, just keep existing data
    } finally {
      setLoading(false);
    }
  }, [user, clearUserCache]);

  // Helper to process and set dashboard metrics and chart data
  const processData = (projects, runs) => {
    console.log('🔍 processData called with:', { projects: projects.length, runs: runs.length });
    
    // Filter projects by selected organization
    let filteredProjects = projects;
    let filteredRuns = runs;
    
    // Ensure we only show organization projects
    filteredProjects = projects.filter(project => 
      project.project_type === 'organization' || 
      (project.organization_name && project.organization_name !== null) ||
      (project.organization_id && project.organization_id !== null)
    );
    
    console.log('🔍 After org filter:', { filteredProjects: filteredProjects.length });
    
    if (selectedOrganization) {
      // Get the organization name from the selected organization
      const selectedOrgName = selectedOrganization.name || selectedOrganization;
      
      // Further filter by specific organization
      filteredProjects = filteredProjects.filter(project => 
        project.organization_id === selectedOrganization.id || 
        project.organization_name === selectedOrgName
      );
      
      console.log('🔍 After specific org filter:', { filteredProjects: filteredProjects.length, selectedOrganization });
    }
    
    // Filter runs to only include those from the filtered projects
    const projectIds = filteredProjects.map(p => p.id || p.project_id);
    console.log('🔍 Project IDs for filtering:', projectIds);
    console.log('🔍 Sample run project_id:', runs[0]?.project_id);
    
    filteredRuns = runs.filter(run => 
      projectIds.includes(run.project_id)
    );
    
    console.log('🔍 After run filtering:', { filteredRuns: filteredRuns.length });
    
    setTotalProjects(filteredProjects.length);
    // Token Usage Data
    const now = new Date();
    let days = 0;
    if (timeFilter === 'Last 7 Days') days = 7;
    else if (timeFilter === 'Last 30 Days') days = 30;
    else if (timeFilter === 'Last 90 Days') days = 90;
    // If 'All Time', show all data
    let tokenUsage = [];
    let totalTokensCount = 0;
    if (days > 0) {
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(now.getDate() - i);
        const dateStr = date.toISOString().slice(0, 10);
        const dayRuns = filteredRuns.filter(run => run.start_time && run.start_time.slice(0, 10) === dateStr);
        const tokens = dayRuns.reduce((sum, run) => {
          // Method 1: Sum from llm_calls (backward compatibility)
          let runTokens = run.llm_calls?.reduce((callSum, call) => callSum + (call.total_tokens || 0), 0) || 0;
          // Method 2: Extract from spans in agent_metrics (onprem pattern - only generation spans)
          if (run.agent_metrics && typeof run.agent_metrics === 'object') {
            Object.values(run.agent_metrics).forEach(metric => {
              if (metric && typeof metric === 'object') {
                // Check trace-level total_tokens
                if (metric.trace && metric.trace.total_tokens) {
                  runTokens += metric.trace.total_tokens || 0;
                }
                // Check spans - only process generation spans (onprem pattern)
                if (metric.spans && Array.isArray(metric.spans)) {
                  metric.spans.forEach(span => {
                    if (span.observation_type === 'generation') {
                      // Priority: total_tokens > usage_details.total > usage_details.total_tokens > input+output
                      if (span.total_tokens) {
                        runTokens += span.total_tokens || 0;
                      } else if (span.usage_details && typeof span.usage_details === 'object') {
                        const usage = span.usage_details;
                        runTokens += usage.total || usage.total_tokens || 
                          ((usage.input || usage.prompt_tokens || usage.input_tokens || 0) + 
                           (usage.output || usage.completion_tokens || usage.output_tokens || 0));
                      } else if (span.input_tokens || span.output_tokens) {
                        runTokens += (span.input_tokens || 0) + (span.output_tokens || 0);
                      }
                    }
                  });
                }
              }
            });
          }
          return sum + runTokens;
        }, 0);
        tokenUsage.push({
          date: dateStr,
          tokens,
          ...(dayRuns.length > 0 && dayRuns[0].llm_calls?.[0]?.cost ? { cost: dayRuns[0].llm_calls[0].cost } : {})
        });
        totalTokensCount += tokens;
      }
    } else {
      // All Time: aggregate by date
      const dateMap = {};
      filteredRuns.forEach(run => {
        if (run.start_time) {
          const dateStr = run.start_time.slice(0, 10);
          if (!dateMap[dateStr]) dateMap[dateStr] = 0;
          // Method 1: Sum from llm_calls (backward compatibility)
          let runTokens = run.llm_calls?.reduce((callSum, call) => callSum + (call.total_tokens || 0), 0) || 0;
          // Method 2: Extract from spans in agent_metrics (onprem pattern - only generation spans)
          if (run.agent_metrics && typeof run.agent_metrics === 'object') {
            Object.values(run.agent_metrics).forEach(metric => {
              if (metric && typeof metric === 'object') {
                // Check trace-level total_tokens
                if (metric.trace && metric.trace.total_tokens) {
                  runTokens += metric.trace.total_tokens || 0;
                }
                // Check spans - only process generation spans (onprem pattern)
                if (metric.spans && Array.isArray(metric.spans)) {
                  metric.spans.forEach(span => {
                    if (span.observation_type === 'generation') {
                      // Priority: total_tokens > usage_details.total > usage_details.total_tokens > input+output
                      if (span.total_tokens) {
                        runTokens += span.total_tokens || 0;
                      } else if (span.usage_details && typeof span.usage_details === 'object') {
                        const usage = span.usage_details;
                        runTokens += usage.total || usage.total_tokens || 
                          ((usage.input || usage.prompt_tokens || usage.input_tokens || 0) + 
                           (usage.output || usage.completion_tokens || usage.output_tokens || 0));
                      } else if (span.input_tokens || span.output_tokens) {
                        runTokens += (span.input_tokens || 0) + (span.output_tokens || 0);
                      }
                    }
                  });
                }
              }
            });
          }
          dateMap[dateStr] += runTokens;
          totalTokensCount += runTokens;
        }
      });
      tokenUsage = Object.entries(dateMap).map(([date, tokens]) => ({ date, tokens }));
    }
    setTokenUsageData(tokenUsage);
    setTotalTokens(formatNumber(totalTokensCount));
    // Average Latency
    // Calculate average latency from ALL LLM calls
    const allLatencies = [];
    filteredRuns.forEach(run => {
      if (run.llm_calls?.length > 0) {
        run.llm_calls.forEach(call => {
          if (call.latency_ms) {
            allLatencies.push(call.latency_ms);
          }
        });
      }
    });
    const avg = allLatencies.length > 0 ? (allLatencies.reduce((a, b) => a + b, 0) / allLatencies.length) : 0;
    setAvgLatency(avg ? `${avg.toFixed(1)} ms` : '0 ms');
    // Project Distribution Data
    const projectRunCount = {};
    
    // First, create a map of project IDs to names
    const projectIdToName = {};
    filteredProjects.forEach(project => {
      const id = project.project_id || project.id || project._id;
      if (id) {
        projectIdToName[id] = project.project || project.name || `Project ${id}`;
      }
    });
    
    filteredRuns.forEach(run => {
      const projectId = run.project_id || 'Unknown';
      const projectName = projectIdToName[projectId] || projectId;
      
      if (!projectRunCount[projectId]) {
        projectRunCount[projectId] = {
          name: projectName,
          projectId: projectId,
          runs: 0,
          totalTokens: 0
        };
      }
      projectRunCount[projectId].runs += 1;
      const tokens = run.llm_calls?.reduce((callSum, call) => callSum + (call.total_tokens || 0), 0) || 0;
      projectRunCount[projectId].totalTokens += tokens;
    });
    
    // Convert to array and sort by total activity (runs + token usage)
    const projectData = Object.values(projectRunCount)
      .map(project => ({
        ...project,
        activityScore: project.runs + (project.totalTokens / 10000) // Weight tokens less than runs
      }))
      .sort((a, b) => b.activityScore - a.activityScore)
      .slice(0, Math.min(8, Object.keys(projectRunCount).length)) // Dynamically adjust based on available space
      .map(({ activityScore, ...rest }) => rest); // Remove temporary score
    setModelDistributionData(projectData);

    // Model-wise Distribution Data - Only use filtered runs for the organization
    const modelUsage = {};
    let totalLLMCalls = 0;
    
    filteredRuns.forEach(run => {
      // Process LLM calls from filtered runs
      if (run.llm_calls && run.llm_calls.length > 0) {
        run.llm_calls.forEach(call => {
          const model = call.model || 'Unknown';
          if (!modelUsage[model]) {
            modelUsage[model] = {
              runs: 0,
              totalTokens: 0,
              totalCost: 0
            };
          }
          modelUsage[model].runs += 1;
          modelUsage[model].totalTokens += call.total_tokens || 0;
          modelUsage[model].totalCost += call.cost || 0;
          totalLLMCalls += 1;
        });
      }
      
      // Also process spans from agent_metrics (onprem pattern - only generation spans)
      if (run.agent_metrics && typeof run.agent_metrics === 'object') {
        Object.values(run.agent_metrics).forEach(metric => {
          if (metric && typeof metric === 'object' && metric.spans && Array.isArray(metric.spans)) {
            metric.spans.forEach(span => {
              // Only process generation spans (aligned with onprem pattern)
              if (span.observation_type === 'generation' && span.model) {
                const model = span.model || 'Unknown';
                if (!modelUsage[model]) {
                  modelUsage[model] = {
                    runs: 0,
                    totalTokens: 0,
                    totalCost: 0
                  };
                }
                modelUsage[model].runs += 1;
                
                // Extract tokens (onprem pattern: total_tokens > usage_details > input+output)
                let tokens = span.total_tokens || 0;
                if (!tokens && span.usage_details && typeof span.usage_details === 'object') {
                  const usage = span.usage_details;
                  tokens = usage.total || usage.total_tokens || 
                    ((usage.input || usage.prompt_tokens || usage.input_tokens || 0) + 
                     (usage.output || usage.completion_tokens || usage.output_tokens || 0));
                } else if (!tokens && (span.input_tokens || span.output_tokens)) {
                  tokens = (span.input_tokens || 0) + (span.output_tokens || 0);
                }
                modelUsage[model].totalTokens += tokens;
                
                // Extract cost (onprem pattern: cost > cost_details.total > cost_details.cost)
                let cost = span.cost || 0;
                if (!cost && span.cost_details) {
                  if (typeof span.cost_details === 'object') {
                    cost = span.cost_details.total || span.cost_details.cost || 0;
                  } else if (typeof span.cost_details === 'number') {
                    cost = span.cost_details;
                  }
                }
                modelUsage[model].totalCost += cost;
                
                totalLLMCalls += 1;
              }
            });
          }
        });
      }
    });
    
    const modelWiseDistribution = Object.entries(modelUsage).map(([model, stats]) => ({
      model,
      runs: stats.runs,
      tokens: stats.totalTokens,
      cost: stats.totalCost,
      percentage: totalLLMCalls > 0 ? (stats.runs / totalLLMCalls * 100).toFixed(1) : 0
    })).sort((a, b) => b.runs - a.runs).slice(0, 10); // Limit to top 10 models to prevent overflow
    
    console.log('Model Distribution Data:', modelWiseDistribution);
    setModelWiseData(modelWiseDistribution);
  };

  useEffect(() => {
    // Always fetch real data from MongoDB
    const loadRealData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Only load data if user is authenticated
        if (!user) {
          setProjectsData([]);
          setRunsData([]);
          processData([], []);
          return;
        }

        // Pass user ID to API calls for authentication with caching
        const projectsResponse = await getProjects(user.id, () => fetchProjects(null, user.id, false));
        const runsResponse = await getRuns(user.id, () => fetchRuns(user.id, false));
        
        // Extract data from Lambda responses (app-data returns projects with nested runs; use .projects only)
        const projects = projectsResponse?.projects || [];
        const runsData = runsResponse?.projects || [];
        
        console.log('🔍 DEBUG - Projects array:', projects.length, projects[0]);
        console.log('🔍 DEBUG - RunsData array:', runsData.length, runsData[0]);
        
        // Extract individual runs from the runs data (which contains projects with nested runs)
        const runs = [];
        runsData.forEach(project => {
          if (project && project.runs && Array.isArray(project.runs)) {
            console.log(`🔍 DEBUG - Processing project: ${project.project}, runs count: ${project.runs.length}`);
            project.runs.forEach(run => {
              if (run) {
                // Add project information to each run
                const runWithProject = {
                  ...run,
                  project_id: project.project_id,
                  project_name: project.project,
                  organization_id: project.organization_id,
                  organization_name: project.organization_name,
                  api_key: project.api_key
                };
                runs.push(runWithProject);
              }
            });
          }
        });
        
        console.log('🔍 DEBUG - Extracted runs:', runs.length);
        console.log('🔍 DEBUG - First run sample:', runs[0]);
        console.log('🔍 DEBUG - First run has llm_calls?', runs[0]?.llm_calls?.length || 0);
        
        setProjectsData(projects);
        setRunsData(runs);
        processData(projects, runs);
      } catch (err) {
        // Don't show error, just set empty data
        setProjectsData([]);
        setRunsData([]);
        processData([], []);
        console.error('Dashboard data loading error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadRealData();
  }, [timeFilter, selectedOrganization, user]);

  // Remove MongoConfigForm and config logic from render
  return (
    <div className="space-y-6 relative min-h-screen">
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
          <div className="bg-white p-6 rounded-lg shadow-lg flex items-center space-x-3 border border-gray-200">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
            <div className="text-gray-700 font-medium">Loading dashboard data...</div>
          </div>
        </div>
      )}

      {/* Organization Warning Banner */}
      <AlwaysShowWarning />

      {/* Dashboard Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 -m-6 p-4 md:p-6 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center border-b border-blue-100 space-y-4 md:space-y-0">
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-4">
          <div>
            <p className="text-gray-600 font-medium">Welcome,</p>
            <WelcomeToArms />
          </div>
          <div className="border-l-0 md:border-l border-blue-200 pl-0 md:pl-4">
            <p className="text-gray-600 font-medium text-sm">Organization</p>
            <OrganizationSelector 
              onOrganizationChange={setSelectedOrganization}
              selectedOrgId={selectedOrganization}
            />
          </div>
        </div>
        <div className="flex items-center space-x-4 w-full md:w-auto">
          <div className="relative flex-1 md:flex-none">
            <select 
              className="border border-gray-300 rounded-lg px-4 py-2 pr-8 appearance-none focus:outline-none bg-white font-medium shadow-sm w-full md:w-auto"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
            >
              <option>All Time</option>
              <option>Last 7 Days</option>
              <option>Last 30 Days</option>
              <option>Last 90 Days</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Get Started Section - Show when there are 0 projects */}
      {!loading && totalProjects === 0 && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 md:p-12 max-w-2xl w-full">
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center border border-blue-100">
                <FiBarChart2 className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Get Started with ARMS</h2>
              <p className="text-lg text-gray-600">
                You don't have any projects yet. Get started by reading our documentation to learn how to set up your first project.
              </p>
            </div>
            <a
              href="https://arms.elsaifoundry.ai/documentation"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              View Documentation
              <FiArrowRight className="ml-2" />
            </a>
          </div>
        </div>
      )}

      {/* Data Analytics Overview Section - Only show if there are projects */}
      {!loading && totalProjects > 0 && (
        <>
      {/* Data Analytics Overview Section */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-4 md:p-6 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-900">Data Analytics Overview</h2>
            <p className="text-sm text-gray-600">Comprehensive insights into your AI model usage and performance</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiShoppingBag className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Projects</p>
                <p className="text-xl font-bold text-gray-900">{totalProjects}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiBarChart2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Tokens (7 days)</p>
                <p className="text-xl font-bold text-gray-900">{totalTokens}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FiClock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Avg. Latency</p>
                <p className="text-xl font-bold text-gray-900">{avgLatency}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Dashboard Grid - Enhanced Visualization Cards */}
      <div className="space-y-6">
        {/* Row 1: Token Usage & Project Distribution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Token Usage Chart */}
          <div className="w-full h-[280px] md:h-[320px] min-h-[280px] md:min-h-[320px]">
          <VisualizationCard 
            title="Token Usage (Last 7 Days)" 
            subtitle={`Total: ${totalTokens} tokens`}
            className="h-full"
          >
            <TokenUsageChart data={tokenUsageData} />
          </VisualizationCard>
        </div>
        {/* Project Distribution */}
          <div className="w-full h-[280px] md:h-[320px] min-h-[280px] md:min-h-[320px]">
          <VisualizationCard 
            title="Project Distribution"
            subtitle="Number of Runs by Project"
            className="h-full"
          >
            <ProjectDistributionChart data={modelDistributionData} projects={projectsData} />
          </VisualizationCard>
        </div>
        </div>
        
        {/* Row 2: Model Distribution & System Resources */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Model-wise Distribution */}
          <div className="w-full h-[280px] md:h-[320px] min-h-[280px] md:min-h-[320px]">
          <VisualizationCard 
            title="Model Distribution"
            subtitle="Usage by AI Model"
            className="h-full"
          >
            <ModelWiseDistributionChart data={modelWiseData} />
          </VisualizationCard>
        </div>
        {/* System Resources */}
          <div className="w-full h-[280px] md:h-[320px] min-h-[280px] md:min-h-[320px]">
          <ResourceUtilization />
        </div>
      </div>


      </div>

      {/* Quick Analytics Links - Enhanced */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <Link to="/cost-analytics" className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 group">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 group-hover:scale-105 transition-transform duration-200">
              <FiDollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Cost Analytics</h3>
              <p className="text-sm text-gray-600">Monitor API usage costs</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors">
            View Details <FiArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link to="/query-analytics" className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 group">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 group-hover:scale-105 transition-transform duration-200">
              <FiMessageSquare className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Query Analytics</h3>
              <p className="text-sm text-gray-600">Analyze prompts & responses</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors">
            View Details <FiArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link to="/log-analytics" className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 group">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 group-hover:scale-105 transition-transform duration-200">
              <FiFileText className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Log Analytics</h3>
              <p className="text-sm text-gray-600">Monitor system logs</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors">
            View Details <FiArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
